import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';

import { toast } from 'sonner';
import { SEO } from '@/components/SEO';
import { Mail, Lock, Loader2, Eye, EyeOff } from 'lucide-react';

const TEST_ACCOUNTS = {
  passenger: { email: 'skater@rollm8s.com', password: 'test123' },
  driver: { email: 'driver@rollm8s.com', password: 'test123' },
};

const LoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const { mode } = useTheme();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  // Auto-login from questionnaire links (?as=passenger or ?as=driver)
  useEffect(() => {
    const autoAs = searchParams.get('as');
    if (autoAs && TEST_ACCOUNTS[autoAs]) {
      const { email, password } = TEST_ACCOUNTS[autoAs];
      setFormData({ email, password });
      // Auto-submit after a brief delay so user sees the fields populate
      const timer = setTimeout(async () => {
        setLoading(true);
        try {
          await login(email, password);
          toast.success(`Logged in as ${autoAs}!`);
          navigate('/dashboard');
        } catch {
          toast.error('Auto-login failed. Please sign in manually.');
        } finally {
          setLoading(false);
        }
      }, 600);
      return () => clearTimeout(timer);
    }
  }, [searchParams, login, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(formData.email, formData.password);
      toast.success('Welcome back!');
      // Honour ?next= so flows like "Sign up to message seller" can land
      // the visitor right back where they started instead of dropping
      // them on the dashboard. Sanitised: only relative paths to keep
      // open-redirect off the table.
      const next = searchParams.get('next');
      const safeNext = next && next.startsWith('/') && !next.startsWith('//') ? next : '/dashboard';
      navigate(safeNext);
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else if (error.request) {
        toast.error('Unable to reach server. Please check your connection.');
      } else {
        toast.error('Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12">
      <SEO
        title="Login"
        description="Log in to RollM8s to access your skate, wheel and board events, tickets, and ride shares."
        path="/login"
        noindex={true}
      />
      <div className="w-full max-w-md space-y-8 animate-slide-up">
        {/* Logo */}
        <div className="text-center">
          <Link to="/" className="inline-flex items-center gap-3">
            <img 
              src={mode === 'dark' ? '/rollm8s-logo-dark.png' : '/rollm8s-logo-light.png'} 
              alt="RollM8s Logo"
              className="w-[6rem] h-[6rem] md:w-[8rem] md:h-[8rem] object-contain"
            />
            <div className="flex flex-col items-start">
              <span className="font-logo text-[2.8rem] md:text-[4rem] tracking-wide leading-tight">
                <span className="text-foreground">ROLL</span>
                <span className="gradient-text">M8S</span>
              </span>
              <span className="text-[8px] md:text-[10px] font-bold uppercase leading-none block w-0 min-w-full" style={{ textAlign: 'justify', textAlignLast: 'justify' }}>
                <span className="text-foreground">THE UK'S FIRST </span>
                <span className="text-primary">SKATER </span>
                <span className="text-secondary">RIDE-SHARE</span>
              </span>
            </div>
          </Link>
        </div>

        <Card className="bg-card border-border/50">
          <CardHeader className="text-center">
            <CardTitle className="font-heading text-2xl">Welcome Back</CardTitle>
            <CardDescription className="text-xl font-bold text-primary animate-blink-vivid">Sign in to continue to <span className="text-foreground">Roll</span>M8s</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Email/Password Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    className="pl-10 h-12 rounded-xl border-2 border-primary"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    data-testid="email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  <Link to="/forgot-password" className="text-sm text-primary hover:underline">
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    className="pl-10 pr-10 h-12 rounded-xl border-2 border-primary"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-14 rounded-xl bg-primary hover:bg-primary/90 font-bold text-xl"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>
            </form>

            <p className="text-center text-sm text-muted-foreground">
              Don't have an account?{' '}
              <Link to="/register" className="text-primary hover:underline">
                Create one
              </Link>
            </p>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-muted-foreground">
          By signing in, you agree to our{' '}
          <a href="#" className="text-primary hover:underline">Terms of Service</a>
          {' '}and{' '}
          <a href="#" className="text-primary hover:underline">Privacy Policy</a>
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
